if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: 1,
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'bullets',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            // Full-bleed snap slideshow on mobile; no peek of next/prev slides
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              spaceBetween: 0,
              slidesPerView: 1,
              centeredSlides: true,
              resistanceRatio: 0.65,
              speed: 300,
            });
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
          case 'sticky-scroll':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              spaceBetween: 0,
              autoHeight: true,
            });
            break;
        }
      }

      updateMediaLayout() {
        // Recreate slider when crossing mobile/desktop so thumbs + snap options stay correct
        this.destroySlider();
        if (typeof this.thumbsInstance === 'object' && this.thumbsInstance) {
          try {
            this.thumbsInstance.slider?.destroy?.(true, true);
          } catch (e) {}
          this.thumbsInstance = false;
        }

        if (FoxTheme.config.mqlMobile) {
          this.initSlider({ useThumbs: this.enableMobileThumbnails });
        } else if (this.enableDesktopSlider) {
          this.initSlider({ useThumbs: true });
        } else {
          this.rewrapStickyScrollForDesktop();
        }
      }

      /**
       * Sticky-scroll uses two column wrappers on desktop. Swiper needs flat
       * .swiper-slide children, so unwrap before init on mobile.
       */
      flattenStickyScrollForMobile() {
        if (this.mediaLayout !== 'sticky-scroll') return;

        const list = this.elements.mediaList;
        if (!list || list.dataset.flattened === 'true') return;

        const stickyCol = list.querySelector('.product__media-sticky-col');
        const scrollCol = list.querySelector('.product__media-scroll-col');
        if (!stickyCol || !scrollCol) return;

        const slides = [...list.querySelectorAll('.product__media-item')];
        slides.forEach((slide) => list.appendChild(slide));
        stickyCol.remove();
        scrollCol.remove();
        list.dataset.flattened = 'true';
      }

      rewrapStickyScrollForDesktop() {
        if (this.mediaLayout !== 'sticky-scroll') return;

        const list = this.elements.mediaList;
        if (!list || list.dataset.flattened !== 'true') return;

        list.querySelectorAll('.swiper-slide-duplicate').forEach((el) => el.remove());

        const slides = [...list.querySelectorAll('.product__media-item')];
        if (!slides.length) return;

        const stickyCol = document.createElement('div');
        stickyCol.className = 'product__media-sticky-col';
        const scrollCol = document.createElement('div');
        scrollCol.className = 'product__media-scroll-col';

        stickyCol.appendChild(slides[0]);
        slides.slice(1).forEach((slide) => scrollCol.appendChild(slide));

        list.replaceChildren(stickyCol, scrollCol);
        list.dataset.flattened = 'false';
      }

      initSlider(options = {}) {
        const useThumbs = options.useThumbs === true;
        const isMobile = FoxTheme.config.mqlMobile;
        const isDesktopVertical = !isMobile && this.mediaLayout === 'vertical-carousel';

        if (typeof this.sliderInstance !== 'object') {
          this.flattenStickyScrollForMobile();

          // Desktop vertical gallery: no loop + no Thumbs module link.
          // Thumb clicks jump instantly; arrows still animate.
          if (isDesktopVertical) {
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              loop: false,
              speed: 300,
              centeredSlides: false,
            });
            delete this.sliderOptions.thumbs;

            this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, []);
            this.sliderInstance.init();
            this.handleSliderAfterInit();
            this.handleSlideChange();
            this.bindThumbnailClicks();
            this.sliderInstance.slider.init();
            this.syncActiveThumbnail(this.sliderInstance.slider.realIndex || 0);
            return;
          }

          // Avoid linking a hidden thumbs swiper on mobile — it breaks slide width / snap
          if (useThumbs && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          } else {
            delete this.sliderOptions.thumbs;
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();
          this.bindThumbnailClicks();

          this.sliderInstance.slider.init();
        }
      }

      /**
       * Desktop thumbs: show the selected image immediately (no slideshow scroll).
       */
      bindThumbnailClicks() {
        if (!this.elements.thumbnails || this._thumbClickBound) return;
        this._thumbClickBound = true;

        this.elements.thumbnails.addEventListener(
          'click',
          (e) => {
            if (FoxTheme.config.mqlMobile && !this.enableMobileThumbnails) return;

            const thumb = e.target.closest('.product__thumbs-item');
            if (!thumb || !this.sliderInstance?.slider) return;

            const items = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item')];
            const index = items.indexOf(thumb);
            if (index < 0) return;

            // Block Swiper Thumbs / other handlers from animating through slides
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            this.jumpToSlide(index);
          },
          true
        );
      }

      jumpToSlide(index) {
        const slider = this.sliderInstance?.slider;
        if (!slider || index < 0) return;

        // Instant jump for thumbs — do not leave transition stuck at 0
        if (typeof slider.setTransition === 'function') slider.setTransition(0);
        if (slider.params.loop) {
          slider.slideToLoop(index, 0, true);
        } else {
          slider.slideTo(index, 0, true);
        }
        if (typeof slider.setTransition === 'function') {
          slider.setTransition(slider.params.speed || 300);
        }
        this.syncActiveThumbnail(index);
      }

      syncActiveThumbnail(index) {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('.product__thumbs-item').forEach((el, i) => {
          el.classList.toggle('swiper-slide-thumb-active', i === index);
        });
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object' && this.sliderInstance) {
          try {
            this.sliderInstance.slider.destroy(true, true);
          } catch (e) {
            try {
              this.sliderInstance.slider.destroy();
            } catch (e2) {}
          }
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            const slider = this.sliderInstance.slider;
            if (slider.realIndex !== currIndex) {
              if (slider.params.loop) {
                slider.slideToLoop(currIndex, 100, false);
              } else {
                slider.slideTo(currIndex, 100, false);
              }
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        const onSlideChange = (swiper) => {
          const { slides, activeIndex, realIndex, thumbs } = swiper;

          // Thumbs module may be absent on desktop (instant thumb jumps)
          if (thumbs?.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          this.syncActiveThumbnail(typeof realIndex === 'number' ? realIndex : activeIndex);

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        };

        this.sliderInstance.slider.on('slideChange', onSlideChange);
        this.sliderInstance.slider.on('realIndexChange', onSlideChange);
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          const slider = this.sliderInstance.slider;
          const index = Math.max(0, slideIndex - 1);
          if (slider.params.loop) {
            slider.slideToLoop(index);
          } else {
            slider.slideTo(index);
          }
          this.syncActiveThumbnail(index);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
